import clsx from 'clsx';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  WEEKDAY_CN,
  addDays,
  cnDate,
  fmtDateWeek,
  fmtMonthDay,
  weekStartOf,
  weekdayOf,
  type TeamInsightDTO,
} from '@wd/shared';
import { ApiError, errorText } from '../../lib/api';
import { timeCN, todayCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import { useLayout } from '../../lib/media';
import { useInsightActions, useTeamStatus } from '../../lib/queries';
import { Icon, MoreIcon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { AIAvatar, Avatar, Button, EmptyState, IconButton, Modal, Quote, Radio, Segmented, Skeleton } from '../../ui/primitives';
import './team.css';

// 团队状态 · 建议关注（设计稿 9a）：“值得认可”在上、“建议沟通”在下，同一套样式，不打分、不排名。
// 只有直属主管能看；AI 只提供观察，判断由主管做出。

const METHOD_LABEL = { '1:1': '1:1', phone: '电话', wechat: '微信' } as const;
const whenCN = (iso: string) => {
  const d = cnDate(new Date(iso));
  return `${d === todayCN() ? '今天' : WEEKDAY_CN[weekdayOf(d)]} ${timeCN(iso)}`;
};

function statusNote(i: TeamInsightDTO): string | null {
  if (i.status === 'talked' && i.talk) return `已沟通 ${fmtMonthDay(i.talk.date)}`;
  if (i.status === 'recognized') return i.thanks ? '已发感谢' : '已认可';
  if (i.status === 'dismissed') return '已移出';
  if (i.meetingAt) return `已约 ${fmtMonthDay(cnDate(new Date(i.meetingAt)))} ${timeCN(i.meetingAt)}`;
  return null;
}

export function TeamPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'mobile';
  const week = params.get('week') ?? undefined;
  const { data, isLoading, error } = useTeamStatus(week);
  const { regenerate } = useInsightActions();
  const selectedId = params.get('i');

  const thisWeek = weekStartOf(todayCN());
  const list = data ? [...data.insights.filter((i) => i.kind === 'recognize'), ...data.insights.filter((i) => i.kind === 'talk')] : [];
  const selected = list.find((i) => i.id === selectedId) ?? (mobile ? undefined : list.find((i) => i.status !== 'dismissed'));
  const idx = selected ? list.indexOf(selected) : -1;
  const pick = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('i', id);
    else next.delete('i');
    setParams(next, { replace: !mobile });
  };
  const goWeek = (ws: string) => setParams(ws === thisWeek ? {} : { week: ws });

  useHotkeys(
    {
      j: () => list[idx + 1] && pick(list[idx + 1].id),
      ArrowDown: () => list[idx + 1] && pick(list[idx + 1].id),
      k: () => idx > 0 && pick(list[idx - 1].id),
      ArrowUp: () => idx > 0 && pick(list[idx - 1].id),
    },
    !mobile,
  );

  if (error) {
    const forbidden = error instanceof ApiError && error.status === 403;
    return (
      <EmptyState icon={forbidden ? 'lock' : 'wifioff'} title={forbidden ? '团队状态只对主管开放' : '团队状态没有加载出来'} desc={errorText(error)}>
        {forbidden && <Button onClick={() => navigate('/radar')}>打开项目雷达</Button>}
      </EmptyState>
    );
  }
  if (isLoading || !data) {
    return (
      <div style={{ padding: '28px 24px' }} aria-busy="true">
        <Skeleton w={200} h={22} />
        <Skeleton w={300} h={12} style={{ marginTop: 12 }} />
      </div>
    );
  }

  const range = `${fmtMonthDay(data.weekStart)} – ${fmtMonthDay(data.weekEnd)}`;
  const isThisWeek = data.weekStart === thisWeek;

  const head = (
    <div className="tm-head">
      <h1 className="t-page-title">{isThisWeek ? '本周建议关注' : `${range} 建议关注`}</h1>
      <div className="tm-meta">
        <Icon name="lock" size={12} stroke={1.4} />
        <span>仅你可见</span>
        <span>·</span>
        <span>基于本周 {data.reportCount} 份日报</span>
        {data.generatedAt && (
          <>
            <span>·</span>
            <span>{whenCN(data.generatedAt)} 生成</span>
          </>
        )}
      </div>
    </div>
  );

  let listBody: ReactNode;
  if (data.directCount === 0) {
    listBody = (
      <EmptyState icon="team" title="你还没有直属成员" desc="在「设置 › 团队与成员」里把成员的直属主管设为你，这里才会有内容。">
        <Button onClick={() => navigate('/settings/members')}>打开团队与成员</Button>
      </EmptyState>
    );
  } else if (regenerate.isPending) {
    listBody = (
      <div style={{ padding: '28px 24px' }} aria-busy="true">
        <div className="t-meta">正在根据本周日报整理，大约需要十几秒…</div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="row" style={{ gap: 12, marginTop: 20 }}>
            <Skeleton w={20} h={20} style={{ borderRadius: '50%' }} />
            <div className="flex1">
              <Skeleton w={80} h={10} />
              <Skeleton w="80%" h={10} style={{ marginTop: 8 }} />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (!list.length) {
    listBody = (
      <EmptyState
        icon="team"
        title={data.draftAt ? `本周名单会在${whenCN(data.draftAt)}生成` : '本周没有需要特别关注的人'}
        desc={
          data.draftAt
            ? '根据本周日报整理，只给你看。也可以现在先看一版。'
            : '名单只在日报里有明确依据时出现。这周没有，说明大家的日报都在正常节奏里。'
        }
      >
        {(data.draftAt || isThisWeek) && (
          <Button onClick={() => regenerate.mutate(data.weekStart)}>{data.draftAt ? '现在生成' : '重新检查一次'}</Button>
        )}
      </EmptyState>
    );
  } else {
    const groups: { kind: 'recognize' | 'talk'; label: string }[] = [
      { kind: 'recognize', label: '值得认可' },
      { kind: 'talk', label: '建议沟通' },
    ];
    listBody = groups.map((g, gi) => {
      const items = list.filter((i) => i.kind === g.kind);
      return (
        <div key={g.kind}>
          <div className="tm-group" style={{ marginTop: gi === 0 ? 28 : 20 }}>
            <span className="name">{g.label}</span>
            <span>{items.filter((i) => i.status !== 'dismissed').length}</span>
          </div>
          <div className="tm-rows">
            {items.length === 0 && <div className="tm-none">这周没有</div>}
            {items.map((i) => {
              const note = statusNote(i);
              return (
                <button
                  key={i.id}
                  type="button"
                  className={clsx('tm-row', selected?.id === i.id && 'sel', i.status === 'dismissed' && 'dim')}
                  onClick={() => pick(i.id)}
                >
                  <Avatar initial={i.member.initial} seed={i.member.name} size={20} />
                  <span className="body">
                    <span className="who">
                      <span className="name">{i.member.name}</span>
                      <span className="t-meta">
                        {i.member.teamName ?? ''}
                        {note ? ` · ${note}` : ''}
                        {i.viaManager ? ` · 直属主管 ${i.viaManager}` : ''}
                      </span>
                    </span>
                    <span className="line">{i.headline}</span>
                  </span>
                  {mobile && <Icon name="chevR" size={14} />}
                </button>
              );
            })}
          </div>
        </div>
      );
    });
  }

  const weekNav = (
    <span className="row" style={{ gap: 2 }}>
      <IconButton name="chevL" label="上一周" mobile={mobile} onClick={() => goWeek(addDays(data.weekStart, -7))} />
      <IconButton name="chevR" label="下一周" mobile={mobile} disabled={isThisWeek} onClick={() => goWeek(addDays(data.weekStart, 7))} />
    </span>
  );

  if (mobile) {
    if (selected) {
      return (
        <div className="tm-m">
          <button type="button" className="tm-m-back" onClick={() => pick(null)}>
            <Icon name="chevL" size={18} color="var(--text-3)" />
            <span>团队状态</span>
          </button>
          <div className="tm-m-scroll">
            <Detail key={selected.id} i={selected} mobile />
          </div>
          <div className="tm-foot">AI 仅提供观察，判断由主管做出。</div>
        </div>
      );
    }
    return (
      <div className="tm-m">
        <div className="tm-m-bar">
          <span className="c2">{range}</span>
          <span className="flex1" />
          {weekNav}
        </div>
        <div className="tm-m-scroll">
          {head}
          {listBody}
        </div>
        <div className="tm-foot">AI 仅提供观察，判断由主管做出。</div>
      </div>
    );
  }

  return (
    <div className="tm">
      <div className="tm-cols">
        <div className={clsx('tm-list', layout !== 'full' && 'narrow')}>
          <div className="tm-bar">
            <div className="wd-crumb">
              <span className="cur">团队状态</span>
              <span className="sep">›</span>
              <span className="prev">{range}</span>
            </div>
            {weekNav}
          </div>
          <div className="tm-list-scroll">
            {head}
            {listBody}
            {list.length > 0 && isThisWeek && (
              <button type="button" className="tm-refresh" disabled={regenerate.isPending} onClick={() => regenerate.mutate(data.weekStart)}>
                <Icon name="refresh" size={12} />
                按最新日报重新整理（已处理的条目不变）
              </button>
            )}
          </div>
        </div>
        <div className="tm-detail">
          {selected ? (
            <Detail key={selected.id} i={selected} mobile={false} />
          ) : (
            <EmptyState icon="team" title={list.length ? '选一个人查看详情' : '这里会显示观察依据和沟通建议'} />
          )}
        </div>
      </div>
      <div className="tm-foot">AI 仅提供观察，判断由主管做出。</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 右侧详情（设计稿 9a 建议沟通 / 19c 值得认可）
// ---------------------------------------------------------------------------

function Detail({ i, mobile }: { i: TeamInsightDTO; mobile: boolean }) {
  const navigate = useNavigate();
  const actions = useInsightActions();
  const [modal, setModal] = useState<'talk' | 'schedule' | 'thanks' | null>(null);
  const [menu, setMenu] = useState(false);
  const [feedback, setFeedback] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const fbRef = useRef<HTMLButtonElement>(null);
  const talk = i.kind === 'talk';
  const done = i.status !== 'open';
  const people = new Set(i.evidence.map((e) => e.memberName));
  const projects = i.member.projects.map((p) => p.code).join('、');

  const buttons = (
    <span className="row" style={{ gap: 6 }}>
      {talk ? (
        <>
          {i.status === 'open' && (
            <Button mobile={mobile} onClick={() => setModal('schedule')}>
              {i.meetingAt ? '改约时间' : '约个 1:1'}
            </Button>
          )}
          <Button mobile={mobile} disabled={i.status === 'talked'} onClick={() => setModal('talk')}>
            已沟通
          </Button>
        </>
      ) : (
        <>
          {!i.thanks && (
            <Button mobile={mobile} icon="send" onClick={() => setModal('thanks')}>
              发一句感谢
            </Button>
          )}
          <Button mobile={mobile} disabled={i.status === 'recognized'} onClick={() => actions.recognize.mutate({ id: i.id, thanks: null })}>
            已认可
          </Button>
        </>
      )}
      <button ref={moreRef} type="button" className={clsx('wd-btn icon', mobile && 'mobile')} aria-label="更多" onClick={() => setMenu(!menu)}>
        <MoreIcon />
      </button>
      <Popover anchor={moreRef} open={menu} onClose={() => setMenu(false)} width={220} align="end">
        <MenuList
          items={[
            ...(done ? [{ value: 'reopen', label: '撤销，放回待处理', icon: <Icon name="refresh" size={14} /> }] : []),
            ...(i.status === 'open' ? [{ value: 'dismiss', label: '移出本周名单', icon: <Icon name="minus" size={14} /> }] : []),
            ...(i.member.projects.length ? [{ value: 'radar', label: `看${i.member.name}负责的项目`, icon: <Icon name="grid" size={14} /> }] : []),
          ]}
          selected={null}
          onPick={(v) => {
            setMenu(false);
            if (v === 'reopen') actions.status.mutate({ id: i.id, status: 'open' });
            if (v === 'dismiss') actions.status.mutate({ id: i.id, status: 'dismissed' });
            if (v === 'radar') navigate(`/projects/${i.member.projects[0].id}`);
          }}
        />
      </Popover>
    </span>
  );

  return (
    <>
      {!mobile && (
        <div className="tm-bar detail">
          <div className="wd-crumb">
            <span className="prev">{talk ? '建议沟通' : '值得认可'}</span>
            <span className="sep">›</span>
            <span className="cur">{i.member.name}</span>
          </div>
          {buttons}
        </div>
      )}
      <div className="tm-detail-scroll">
        <div className="tm-in">
          <div className="tm-person">
            <Avatar initial={i.member.initial} seed={i.member.name} size={32} />
            <div className="col" style={{ gap: 2 }}>
              <button type="button" className="name tm-name" onClick={() => navigate(`/people/${i.member.id}`)} title="打开成员页">
                {i.member.name}
              </button>
              <span className="t-meta">
                {i.member.teamName ?? ''}
                {projects ? ` · 负责 ${projects}` : ''}
              </span>
            </div>
          </div>
          {mobile && <div style={{ marginTop: 16 }}>{buttons}</div>}

          <div className="tm-obs">
            <span className="ic">{i.generatedBy === 'ai' ? <AIAvatar size={16} /> : <Icon name="doc" size={14} />}</span>
            <div className="text">{i.observation}</div>
          </div>

          {(i.status !== 'open' || i.meetingAt) && <StatusBlock i={i} />}

          <Section title={talk ? '观察依据' : '依据'} hint={talk ? '日报原文' : `${people.size} 人的日报`}>
            <div className="col" style={{ gap: 12, marginTop: 4 }}>
              {i.evidence.map((e, k) => (
                <Quote key={k} meta={[talk && e.memberName === i.member.name ? null : e.memberName, fmtDateWeek(e.date), e.label === '日报' ? null : e.label].filter(Boolean).join(' · ')}>
                  {e.text}
                </Quote>
              ))}
            </div>
          </Section>

          {talk && i.positives.length > 0 && (
            <Section title="同期正面信号">
              {i.positives.map((p, k) => (
                <div key={k} className="tm-pos">
                  <span className="d">{fmtMonthDay(p.date)}</span>
                  <span>{p.text}</span>
                </div>
              ))}
            </Section>
          )}

          {!talk && (i.impact || i.related.length > 0) && (
            <Section title={i.impact ? '影响' : '相关项目'}>
              {i.impact ? (
                <div className="tm-impact">{i.impact}</div>
              ) : (
                <div className="tm-impact">{i.related.map((p) => `${p.code} ${p.name}`).join('、')}</div>
              )}
            </Section>
          )}

          <Section title={talk ? '建议的沟通切入点' : '建议的认可方式'}>
            <div className="tm-sugs">
              {i.suggestions.map((s, k) => (
                <div key={k} className="tm-sug">
                  <span className="n">{k + 1}</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </Section>

          {!talk && !i.thanks && <div className="tm-note">「发一句感谢」会以你的名义发到{i.member.name}的微信，不经过 AI 改写。</div>}
          <div className="tm-note">
            {i.feedbackGiven ? (
              '已收到你的反馈，之后会据此调整。'
            ) : (
              <>
                这条观察不准确？
                <button ref={fbRef} type="button" className="tm-link" onClick={() => setFeedback(true)}>
                  告诉 AI
                </button>
              </>
            )}
          </div>
          <FeedbackPopover anchor={fbRef} open={feedback} onClose={() => setFeedback(false)} id={i.id} />
        </div>
      </div>
      {modal === 'talk' && <TalkModal i={i} onClose={() => setModal(null)} />}
      {modal === 'schedule' && <ScheduleModal i={i} onClose={() => setModal(null)} />}
      {modal === 'thanks' && <ThanksModal i={i} onClose={() => setModal(null)} />}
    </>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="tm-sec">
      <div className="tm-sec-head">
        <span className="name">{title}</span>
        {hint && <span>{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/** 已处理的状态：沟通记录（仅自己可见）、已发的感谢、约好的时间 */
function StatusBlock({ i }: { i: TeamInsightDTO }) {
  return (
    <div className="tm-status">
      {i.meetingAt && i.status === 'open' && (
        <div className="row" style={{ gap: 8 }}>
          <Icon name="cal" size={14} />
          <span>
            已约 {fmtDateWeek(cnDate(new Date(i.meetingAt)))} {timeCN(i.meetingAt)} 聊聊，{i.member.name}已收到通知
          </span>
        </div>
      )}
      {i.status === 'talked' && i.talk && (
        <div className="col" style={{ gap: 4 }}>
          <span className="row" style={{ gap: 8 }}>
            <Icon name="check" size={14} />
            <span>
              {fmtDateWeek(i.talk.date)} · {METHOD_LABEL[i.talk.method]}沟通过 · {i.talk.nextWeek === 'mute_4_weeks' ? '同类情况 4 周内不再提示' : '下周继续观察'}
            </span>
          </span>
          {i.talk.note && <span className="tm-status-note">{i.talk.note}</span>}
          <span className="t-meta">备注仅你可见，不会给{i.member.name}看，AI 也不会读取</span>
        </div>
      )}
      {i.status === 'recognized' && (
        <div className="col" style={{ gap: 4 }}>
          <span className="row" style={{ gap: 8 }}>
            <Icon name="check" size={14} />
            <span>{i.thanks ? `${whenCN(i.thanks.at)} 已发感谢` : '已认可'}</span>
          </span>
          {i.thanks && <span className="tm-status-note">{i.thanks.text}</span>}
        </div>
      )}
      {i.status === 'dismissed' && (
        <span className="row" style={{ gap: 8 }}>
          <Icon name="minus" size={14} />
          <span>已移出本周名单</span>
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 弹层：记录这次沟通（19b）、约个 1:1、发一句感谢（19c）、告诉 AI
// ---------------------------------------------------------------------------

function TalkModal({ i, onClose }: { i: TeamInsightDTO; onClose: () => void }) {
  const { talk } = useInsightActions();
  const [date, setDate] = useState(todayCN());
  const [method, setMethod] = useState<'1:1' | 'phone' | 'wechat'>('1:1');
  const [note, setNote] = useState('');
  const [next, setNext] = useState<'keep_watching' | 'mute_4_weeks'>('keep_watching');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      width={540}
      title="记录这次沟通"
      subtitle={i.member.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={talk.isPending}
            onClick={() => talk.mutate({ id: i.id, date, method, note: note.trim() || null, nextWeek: next }, { onSuccess: onClose, onError: (e) => setError(errorText(e)) })}
          >
            保存
          </Button>
        </>
      }
    >
      <div className="tm-form">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <span className="k">时间</span>
          <input className="wd-input" type="date" value={date} max={todayCN()} onChange={(e) => setDate(e.target.value)} aria-label="沟通日期" style={{ width: 160 }} />
          <span style={{ width: 12 }} />
          <span className="k">方式</span>
          <Segmented
            items={[
              { value: '1:1', label: '1:1' },
              { value: 'phone', label: '电话' },
              { value: 'wechat', label: '微信' },
            ]}
            value={method}
            onChange={setMethod}
          />
        </div>
        <div>
          <div className="tm-form-label">
            备注 <span>· 仅你可见，不会给{i.member.name}看，AI 也不会读取</span>
          </div>
          <textarea className="wd-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="聊了什么、接下来怎么做，例如：返修集中在宁波客户，备件等了一周，已让采购本周补货。" />
        </div>
        <div>
          <div className="tm-form-label">下周</div>
          {(
            [
              ['keep_watching', '继续观察', '如果情况没有变化，下周仍会出现在建议沟通里'],
              ['mute_4_weeks', '已了解，不再提示', '同类情况 4 周内不再出现；有新情况时照常提示'],
            ] as const
          ).map(([v, t, d]) => (
            <button key={v} type="button" className={clsx('tm-choice', next === v && 'on')} onClick={() => setNext(v)}>
              <Radio checked={next === v} />
              <span className="col" style={{ gap: 2 }}>
                <span className="c1">{t}</span>
                <span className="t-meta">{d}</span>
              </span>
            </button>
          ))}
        </div>
        {error && <div className="t-meta c-red">{error}</div>}
      </div>
    </Modal>
  );
}

function ScheduleModal({ i, onClose }: { i: TeamInsightDTO; onClose: () => void }) {
  const { schedule } = useInsightActions();
  const [date, setDate] = useState(addDays(todayCN(), 1));
  const [time, setTime] = useState('14:00');
  const [text, setText] = useState('想和你聊聊最近的工作，看看有什么需要支持的。');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      width={480}
      title="约个 1:1"
      subtitle={i.member.name}
      onClose={onClose}
      footNote="对方看不到这条观察"
      footer={
        <Button
          variant="primary"
          disabled={schedule.isPending || !date || !time}
          onClick={() =>
            schedule.mutate(
              { id: i.id, at: new Date(`${date}T${time}:00+08:00`).toISOString(), text },
              { onSuccess: onClose, onError: (e) => setError(errorText(e)) },
            )
          }
        >
          发送邀请
        </Button>
      }
    >
      <div className="tm-form">
        <div className="row" style={{ gap: 8 }}>
          <input className="wd-input" type="date" value={date} min={todayCN()} onChange={(e) => setDate(e.target.value)} aria-label="日期" style={{ width: 160 }} />
          <input className="wd-input" type="time" value={time} step={900} onChange={(e) => setTime(e.target.value)} aria-label="时间" style={{ width: 110 }} />
        </div>
        <div>
          <div className="tm-form-label">带一句话</div>
          <textarea className="wd-textarea" rows={2} value={text} maxLength={300} onChange={(e) => setText(e.target.value)} />
        </div>
        <span className="t-meta">会以你的名义发到{i.member.name}的微信和收件箱，只写时间和这句话。</span>
        {error && <div className="t-meta c-red">{error}</div>}
      </div>
    </Modal>
  );
}

function ThanksModal({ i, onClose }: { i: TeamInsightDTO; onClose: () => void }) {
  const { recognize } = useInsightActions();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      width={480}
      title="发一句感谢"
      subtitle={i.member.name}
      onClose={onClose}
      footNote="原样发出，不经过 AI 改写"
      footer={
        <Button variant="primary" disabled={!text.trim() || recognize.isPending} onClick={() => recognize.mutate({ id: i.id, thanks: text }, { onSuccess: onClose, onError: (e) => setError(errorText(e)) })}>
          发送
        </Button>
      }
    >
      <div className="tm-form">
        <textarea
          className="wd-textarea"
          rows={4}
          autoFocus
          maxLength={300}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`说清楚是哪件事、带来了什么，例如：谢谢你${i.headline.length < 30 ? `${i.headline.replace(/[。，]$/u, '')}` : '这周的配合'}，这让项目节点有了余量。`}
        />
        <span className="t-meta">会以你的名义发到{i.member.name}的微信和收件箱。</span>
        {error && <div className="t-meta c-red">{error}</div>}
      </div>
    </Modal>
  );
}

const FEEDBACK = ['不是事实', '有合理原因，不需要关注', '不该由 AI 判断', '其他'];

function FeedbackPopover({ anchor, open, onClose, id }: { anchor: React.RefObject<HTMLElement | null>; open: boolean; onClose: () => void; id: string }) {
  const { feedback } = useInsightActions();
  const [reason, setReason] = useState<string | null>(null);
  const [text, setText] = useState('');
  useEffect(() => {
    if (!open) {
      setReason(null);
      setText('');
    }
  }, [open]);
  return (
    <Popover anchor={anchor} open={open} onClose={onClose} width={300}>
      <form
        className="wd-pop-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason) feedback.mutate({ id, reason, text: text.trim() || null }, { onSuccess: onClose });
        }}
      >
        <span className="t-group">哪里不对</span>
        <div className="tm-chips">
          {FEEDBACK.map((r) => (
            <button key={r} type="button" className={clsx('tm-chip', reason === r && 'on')} onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
        <input className="wd-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="补充一句（可选）" />
        <div className="actions">
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" type="submit" disabled={!reason || feedback.isPending}>
            提交
          </Button>
        </div>
      </form>
    </Popover>
  );
}

